import { ContextualMenu, FontSizes, FontWeights, IColumn, IContextualMenuItem, ILabelStyles, Label, Selection, ShimmeredDetailsList, Spinner, SpinnerSize, Stack, TooltipHost } from '@fluentui/react'
import React from 'react'
import { SubscriptionIntervalEnum, SubscriptionTierEnum } from '../data-structures/enums';
import { LanguageContext } from '../utilities/LocalizationModule';
import SubscriptionStatusPill from './SubscriptionStatusPill';
import { useDetailsListContextMenu } from '../hooks/useDetailsListContextMenu';

export const subheadingStyle: Partial<ILabelStyles> = {
    root: {
        fontWeight: FontWeights.regular,
        fontSize: FontSizes.xLarge,
    }
}

type Props = {
    items: any[];
    isLoading: boolean;
    itemSelected: (items: any[]) => void;
    deselect: boolean;
    isMultiTenant: boolean;
    filterText?: string;
    contextMenuItems?: IContextualMenuItem[];
}

const SubscriptionsList = ({ items, isLoading, itemSelected, deselect, isMultiTenant, filterText, contextMenuItems }: Props) => {
    const languageStrings = React.useContext(LanguageContext);

    const [cols, setCols] = React.useState<IColumn[]>([]);
    const columnClickedRef = React.useRef<{ key: string, isSorted: boolean, isSortedDescending: boolean }>();

    const [filteredItems, setFilteredItems] = React.useState<any[]>(items)

    React.useEffect(() => {
        filterItems(filterText?.trim());
    }, [filterText, items]);

    const filterItems = (filter: string) => {
        if (!filter) {
            setFilteredItems(items);
        } else {
            const resultProperties = ["tenantName", "createdDate", "status", "interval", "product", "description", "availableLicenses", "assignedLicenses", "expiryDate", "paymentPlatform"];

            const newFilteredItems = items.filter(item =>
                resultProperties.some(prop =>
                    item[prop] && item[prop].toString().toLowerCase().includes(filter.toLowerCase())
                )
            );

            setFilteredItems(newFilteredItems);
        }
    }

    React.useEffect(() => {
        if (isMultiTenant) {
            const tenantNameCol: IColumn = {
                key: 'tenantName',
                name: languageStrings.Tenant,
                fieldName: 'tenantName',
                data: "string",
                minWidth: 50,
                maxWidth: 200,
                isResizable: true,
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "tenantName" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "tenantName" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick
            }

            const newCols = [...tenantSubscriptionCols];
            newCols.splice(1, 0, tenantNameCol);
            setCols(newCols);
        } else {
            setCols(tenantSubscriptionCols);
        }

    }, [isMultiTenant])

    function copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
        try {
            const key = columnKey as keyof T;
            return items.slice(0).sort((a: T, b: T) => {
                return ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1)
            });
        } catch (error) {
            console.error("copyAndSort: ", error);
            return items;
        }
    };

    React.useEffect(() => {
        _selection.current.setAllSelected(false);
    }, [deselect])

    const _getSelectionDetails = () => {
        itemSelected(_selection.current.getSelection());
    };

    const _selection = React.useRef(new Selection({
        // Track items by stable id, not by reference. Required so that selection
        // survives item-array replacement (every refresh produces new object
        // identities; the polling path replaces items every ~2.5s while a pending
        // quantity change is in flight). Without this, the selected row would
        // deselect on every refresh and the command bar actions would vanish.
        getKey: (item: any) => item?.id?.toString() ?? '',
        onSelectionChanged: _getSelectionDetails
    }));

    const _onColumnClick = React.useCallback((_ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
        try {
            setCols(prevCols => {
                const newColumns = prevCols.slice();
                const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];

                newColumns.forEach((newCol: IColumn) => {
                    if (newCol === currColumn) {
                        currColumn.isSortedDescending = !currColumn.isSortedDescending;
                        currColumn.isSorted = true;

                        columnClickedRef.current = {
                            key: currColumn.key,
                            isSorted: currColumn.isSorted,
                            isSortedDescending: currColumn.isSortedDescending
                        };
                    } else {
                        newCol.isSorted = false;
                        newCol.isSortedDescending = true;
                    }
                });

                setFilteredItems(prevFiltered => {
                    return copyAndSort(prevFiltered, currColumn.fieldName!, currColumn.isSortedDescending);
                });

                return newColumns;
            });


        } catch (error) {
            console.error("onColumnClick: ", error);
        }
    }, [filteredItems, cols, filterText]);

    const tenantSubscriptionCols: IColumn[] = [
        {
            key: 'createdDate',
            name: languageStrings.Created,
            fieldName: 'createdDate',
            data: "string",
            minWidth: 120,
            maxWidth: 120,
            isResizable: true,
            onRender(item, index, column) {
                // createdDateText is pre-formatted in ApiHelper from the UTC-correct instant
                // (parsed with a 'Z'), so the cell renders in the viewer's local timezone.
                return item.createdDateText;
            },
            isSorted: columnClickedRef.current && columnClickedRef.current.key === "createdDate" ? columnClickedRef.current.isSorted : false,
            isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "createdDate" ? columnClickedRef.current.isSortedDescending : false,
            onColumnClick: _onColumnClick
        },
        {
            key: 'status',
            name: languageStrings.Status,
            fieldName: 'status',
            data: "string",
            // Wide enough for the longest pill label ("Awaiting payment · due 30 Sep 2027" and its nl/de
            // variants) at 12px semibold plus icon: Fluent lays every column out at minWidth and clips the
            // cell with an ellipsis, so the previous 150/200 cut the pill mid-word.
            minWidth: 180,
            maxWidth: 260,
            isResizable: true,
            onRender(item) {
                return (
                    <SubscriptionStatusPill
                        status={item.status}
                        tier={item.tier}
                        cancelAtRaw={item.cancelAtRaw}
                        expiryDateRaw={item.expiryDateRaw}
                        openInvoiceId={item.openInvoiceId}
                        openInvoiceDueAtRaw={item.openInvoiceDueAtRaw}
                    />
                );
            },
            isSorted: columnClickedRef.current && columnClickedRef.current.key === "status" ? columnClickedRef.current.isSorted : false,
            isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "status" ? columnClickedRef.current.isSortedDescending : false,
            onColumnClick: _onColumnClick
        },
        {
            key: 'interval',
            name: languageStrings.Frequency,
            fieldName: 'interval',
            data: "string",
            minWidth: 85,
            maxWidth: 100,
            isResizable: true,
            isSorted: columnClickedRef.current && columnClickedRef.current.key === "interval" ? columnClickedRef.current.isSorted : false,
            isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "interval" ? columnClickedRef.current.isSortedDescending : false,
            onColumnClick: _onColumnClick
        },
        {
            key: 'product',
            name: languageStrings.Product,
            fieldName: 'product',
            data: "string",
            minWidth: 130,
            maxWidth: 130,
            isResizable: true,
            isSorted: columnClickedRef.current && columnClickedRef.current.key === "product" ? columnClickedRef.current.isSorted : false,
            isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "product" ? columnClickedRef.current.isSortedDescending : false,
            onColumnClick: _onColumnClick
        },
        {
            key: 'description',
            name: languageStrings.Description,
            fieldName: 'description',
            data: "string",
            minWidth: 120,
            maxWidth: 350,
            isResizable: true,
            isSorted: columnClickedRef.current && columnClickedRef.current.key === "description" ? columnClickedRef.current.isSorted : false,
            isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "description" ? columnClickedRef.current.isSortedDescending : false,
            onColumnClick: _onColumnClick
        },
        {
            key: 'availableLicences',
            name: languageStrings.AvailableLicences,
            fieldName: 'availableLicenses',
            data: "string",
            minWidth: 130,
            maxWidth: 240,
            isResizable: true,
            onRender(item) {
                if (item._pendingQuantityChange == null) {
                    return item.availableLicenses;
                }
                const badge = languageStrings.PendingStripeConfirmation.replace('{count}', item._pendingQuantityChange.toString());
                return (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <span>{item.availableLicenses}</span>
                        <TooltipHost content={languageStrings.PendingStripeConfirmationTooltip}>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                                <Spinner size={SpinnerSize.xSmall} />
                                <span style={{ fontSize: 11, color: 'var(--db-fg-muted)', whiteSpace: 'nowrap' }}>{badge}</span>
                            </Stack>
                        </TooltipHost>
                    </Stack>
                );
            },
            isSorted: columnClickedRef.current && columnClickedRef.current.key === "availableLicences" ? columnClickedRef.current.isSorted : false,
            isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "availableLicences" ? columnClickedRef.current.isSortedDescending : false,
            onColumnClick: _onColumnClick
        },
        {
            key: 'assignedLicenses',
            name: languageStrings.AssignedLicences,
            fieldName: 'assignedLicenses',
            data: "string",
            minWidth: 130,
            maxWidth: 130,
            isResizable: true,
            isSorted: columnClickedRef.current && columnClickedRef.current.key === "assignedLicenses" ? columnClickedRef.current.isSorted : false,
            isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "assignedLicenses" ? columnClickedRef.current.isSortedDescending : false,
            onColumnClick: _onColumnClick
        },
        {
            key: 'expiry',
            name: languageStrings.ExpiryDate,
            fieldName: 'expiryDateRaw',
            data: "string",
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender(item, index, column) {
                return item.expiryDate;
            },
            isSorted: columnClickedRef.current && columnClickedRef.current.key === "expiry" ? columnClickedRef.current.isSorted : false,
            isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "expiry" ? columnClickedRef.current.isSortedDescending : false,
            onColumnClick: _onColumnClick
        },
        {
            key: 'paymentPlatform',
            name: languageStrings.PaymentPlatform,
            fieldName: 'paymentPlatform',
            data: "string",
            minWidth: 150,
            maxWidth: 150,
            isResizable: true,
            isSorted: columnClickedRef.current && columnClickedRef.current.key === "paymentPlatform" ? columnClickedRef.current.isSorted : false,
            isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "paymentPlatform" ? columnClickedRef.current.isSortedDescending : false,
            onColumnClick: _onColumnClick
        },
    ]

    const { contextMenuProps, onItemContextMenu } = useDetailsListContextMenu(
        _selection.current,
        contextMenuItems ?? []
    );

    return (
        <Stack>
            <ShimmeredDetailsList
                setKey="items"
                items={filteredItems || []}
                columns={cols}
                selection={_selection.current}
                enableShimmer={isLoading}
                onItemContextMenu={onItemContextMenu}
            />
            {contextMenuProps && <ContextualMenu {...contextMenuProps} />}
        </Stack>
    )
}

export default SubscriptionsList